
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { ApiError, apiPath } from "@/api/client";
import { useRoleBrief, useRoleBriefs, useRoleCandidates, useSearchRuns, useSourcedProfiles, useSourcingTasks } from "@/api/hooks/admin";
import {
  useAiEvaluateSourcedProfile,
  useAnswerRoleClarification,
  useArchiveRoleBrief,
  useCancelSourcingTask,
  useCreateOutreachApproval,
  useCreateSearchRun,
  useCreateSourcedProfile,
  useDismissRoleClarification,
  usePromoteSourcedProfile,
  useReopenRoleClarification,
  useRetrySourcingTask,
  useSkipSourcedProfile,
  useSourceOneProfile,
  useUpdateCandidate,
} from "@/api/hooks/mutations";
import type {
  Candidate,
  CandidateRepresentationStatus,
  RoleBrief,
  RoleClarification,
  SearchRun,
  SourcedProfile,
  SourcingTask,
} from "@/api/endpoints";
import { Button } from "@/components/ui/Button";
import { Field, fieldInputClass } from "@/components/ui/Field";
import { PaginationControls } from "@/components/ui/PaginationControls";
import { StatusTag } from "@/components/ui/StatusTag";
import { fmtDate, humanize } from "@/lib/format";
import { safeUrl } from "@/lib/safe-url";

type SourcingProvider = "linkedin_xray_serp" | "linkedin";
type ClarificationAction = "answer" | "dismiss" | "reopen";
type ClarificationPatchEntry = {
  field: string;
  before: string;
  after: string;
};
type ClarificationRow = RoleClarification & {
  isBlocking: boolean;
  whyMatters: string;
  targetFields: string[];
};

const CLARIFICATION_PATCH_STORAGE_PREFIX = "ngon.role-clarification-patch";
const BLOCKING_CLARIFICATION_FIELDS = new Set(["acceptable_geos", "budget", "must_have_skills"]);
const OPTIONAL_CLARIFICATION_FIELDS = new Set(["start_date", "client_name_share_allowed", "clarification_audit"]);

export function AdminRoleRailPage() {
  const params = useParams();
  const detailId = params.id ? parseInt(params.id, 10) : null;
  return detailId != null ? <RoleBriefDetail id={detailId} /> : <RoleBriefList />;
}

function RoleBriefList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "1", 10);
  const list = useRoleBriefs({ page, page_size: 50 });

  function setPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Role rail</h1>
        <p className="mt-1 text-sm text-text-muted">
          Intake, source, evaluate, promote, and approval-gate candidates for each role.
        </p>
      </div>
      <div className="rounded-2xl border border-border bg-surface">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold">Role briefs</h2>
        </div>
        <div className="divide-y divide-border">
          {list.isLoading ? (
            <EmptyState>Loading role briefs...</EmptyState>
          ) : !list.data?.items.length ? (
            <EmptyState>No role briefs yet. Open a merchant request and run intake.</EmptyState>
          ) : (
            list.data.items.map((role) => {
                const rows = deriveClarificationRows(role, role.clarifications ?? [], role.missing_questions ?? []);
                const unmetBlockingCount = rows.filter((row) => row.isBlocking && isClarificationOpen(row.status)).length;
                const openOptionalCount = rows.filter((row) => !row.isBlocking && isClarificationOpen(row.status)).length;
                return (
              <Link
                key={role.id}
                to={"/admin/role-rail/" + role.id}
                className="grid gap-3 px-5 py-4 hover:bg-surface-alt md:grid-cols-[minmax(0,1fr)_160px]"
              >
                <div>
                  <div className="font-semibold">{role.role_title}</div>
                  <div className="mt-1 text-sm text-text-muted">
                    {role.client_company} · {humanize(role.service_type, "service")} · {(role.acceptable_geos ?? []).join(", ") || "geo missing"}
                  </div>
                  {unmetBlockingCount > 0 ? (
                    <div className="mt-2 text-sm text-[#854d0e]">
                      {unmetBlockingCount} blocker{unmetBlockingCount === 1 ? "" : "s"} before readiness
                    </div>
                  ) : openOptionalCount > 0 ? (
                    <div className="mt-2 text-sm text-text-muted">
                      {openOptionalCount} optional clarification{openOptionalCount === 1 ? "" : "s"} still open
                    </div>
                  ) : null}
                </div>
                <div className="md:text-right">
                  <StatusTag status={role.status} />
                  <div className="mt-2 text-xs text-text-muted">{fmtDate(role.updated_at)}</div>
                </div>
              </Link>
                );
              })
          )}
        </div>
      </div>
      {list.data && list.data.total > list.data.page_size ? (
        <PaginationControls
          page={list.data.page}
          pageSize={list.data.page_size}
          total={list.data.total}
          itemLabel="role briefs"
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}

function RoleBriefDetail({ id }: { id: number }) {
  const navigate = useNavigate();
  const role = useRoleBrief(id);
  const runs = useSearchRuns(id);
  const candidates = useRoleCandidates(id);
  const createRun = useCreateSearchRun();
  const archive = useArchiveRoleBrief();
  const [sourceProvider, setSourceProvider] = useState<SourcingProvider>("linkedin_xray_serp");
  const [query, setQuery] = useState("");
  const [locations, setLocations] = useState("");
  const [geoUrns, setGeoUrns] = useState("");
  const [searchUrl, setSearchUrl] = useState("");
  const [filters, setFilters] = useState("");
  const [seededRoleId, setSeededRoleId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<CleanupAction>(null);

  useEffect(() => {
    if (!role.data || seededRoleId === role.data.id) return;
    setQuery(defaultSearchQuery(role.data));
    setLocations((role.data.acceptable_geos ?? []).join("\n"));
    setGeoUrns("");
    setSearchUrl("");
    setFilters("");
    setSourceProvider("linkedin_xray_serp");
    setSeededRoleId(role.data.id);
  }, [role.data, seededRoleId]);

  const roleSkills = role.data?.must_have_skills ?? [];
  const roleSkillQuery = role.data ? defaultSearchQuery(role.data) : "";
  const browserSkillQuery = role.data ? browserFallbackSearchQuery(role.data) : "";

  function resetSearchDraft() {
    if (!role.data) return;
    setSourceProvider("linkedin_xray_serp");
    setQuery(roleSkillQuery);
    setLocations((role.data.acceptable_geos ?? []).join("\n"));
    setGeoUrns("");
    setSearchUrl("");
    setFilters("");
  }

  function onSourceProviderChange(provider: SourcingProvider) {
    setSourceProvider(provider);
    if (!role.data) return;
    setQuery(provider === "linkedin" ? browserFallbackSearchQuery(role.data) : defaultSearchQuery(role.data));
  }

  async function onCreateRun() {
    const effectiveQuery = query.trim() || (sourceProvider === "linkedin" ? browserSkillQuery : roleSkillQuery);
    if (!effectiveQuery) {
      toast.error("Search query is required");
      return;
    }
    if (!searchUrl.trim()) {
      if (sourceProvider === "linkedin") {
        if (roleSkills.length && !queryMatchesAnySkill(effectiveQuery, roleSkills)) {
          toast.error("LinkedIn browser fallback query should include at least one role skill");
          return;
        }
      } else {
        const missing = missingQuerySkills(effectiveQuery, roleSkills);
        if (missing.length) {
          toast.error("Search query is missing role skill: " + missing.join(", "));
          return;
        }
      }
    }
    try {
      await createRun.mutateAsync({
        roleBriefId: id,
        body: {
          source: sourceProvider,
          search_query: effectiveQuery,
          source_filters: {
            locations: splitLines(locations),
            geo_urns: sourceProvider === "linkedin" ? splitLines(geoUrns) : [],
            search_url: sourceProvider === "linkedin" ? searchUrl.trim() || null : null,
            note: filters.trim() || null,
          },
          notes: "One-profile-at-a-time sourcing run.",
        },
      });
      setQuery("");
      setLocations("");
      setGeoUrns("");
      setSearchUrl("");
      setFilters("");
      setSourceProvider("linkedin_xray_serp");
      setSeededRoleId(null);
      toast.success("Search run created");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create search run");
    }
  }

  async function onArchive() {
    try {
      await archive.mutateAsync(id);
      toast.success("Role brief archived");
      navigate("/admin/role-rail");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Archive failed");
    }
  }

  if (role.isLoading) return <EmptyState>Loading role rail...</EmptyState>;
  if (role.error) {
    return (
      <EmptyState tone="danger">
        {role.error instanceof ApiError && role.error.status === 404
          ? "This role brief no longer exists."
          : role.error instanceof Error
            ? role.error.message
            : "Could not load this role brief."}
      </EmptyState>
    );
  }
  if (!role.data) return <EmptyState>This role brief is unavailable.</EmptyState>;
  const r = role.data;
  const mustHaveSkills = r.must_have_skills ?? [];
  const acceptableGeos = r.acceptable_geos ?? [];
  const missingQuestions = r.missing_questions ?? [];
  const clarifications = r.clarifications ?? [];
  const clarificationRows = deriveClarificationRows(r, clarifications, missingQuestions);
  const unmetBlockingClarifications = clarificationRows.filter((row) => row.isBlocking && isClarificationOpen(row.status));
  const openOptionalClarifications = clarificationRows.filter((row) => !row.isBlocking && isClarificationOpen(row.status));
  const latestRun = runs.data?.items[0] ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/admin/role-rail" className="text-sm font-medium text-brand hover:underline">
            Back to role rail
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">{r.role_title}</h1>
          <p className="mt-1 text-sm text-text-muted">
            {r.client_company} · {humanize(r.service_type, "service")} · <StatusTag status={r.status} />
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Link to={"/admin/requests/" + r.merchant_request_id} className="rounded-md border border-border px-3 py-2 text-sm font-medium">
            Open request
          </Link>
          <Button variant="secondary" onClick={() => setConfirmAction("archive")}>
            Archive role brief
          </Button>
        </div>
      </div>

      {confirmAction && (
        <CleanupConfirm
          noun="role brief"
          itemName={r.role_title}
          loading={archive.isPending}
          onCancel={() => setConfirmAction(null)}
          onConfirm={onArchive}
        />
      )}

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-2xl border border-border bg-surface p-5">
          <h2 className="text-base font-semibold">Step 1: Intake brief</h2>
          <div className={"mt-4 rounded-xl border p-4 " + (unmetBlockingClarifications.length
            ? "border-[#fde68a] bg-warning-soft"
            : "border-[#bbf7d0] bg-success-soft")}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-text">Clarification readiness</div>
                <div className="mt-1 text-sm text-text-muted">
                  {unmetBlockingClarifications.length
                    ? "Blocked until the sourcing-critical clarifications below are answered."
                    : openOptionalClarifications.length
                      ? "Ready for sourcing. Optional clarification follow-up can stay open without blocking the rail."
                      : "Ready for sourcing. No unmet clarification blockers remain."}
                </div>
              </div>
              <span
                className={"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold " + (unmetBlockingClarifications.length
                  ? "bg-[#fef3c7] text-[#92400e]"
                  : "bg-white/80 text-[#166534]")}
              >
                {unmetBlockingClarifications.length
                  ? `${unmetBlockingClarifications.length} unmet blocker${unmetBlockingClarifications.length === 1 ? "" : "s"}`
                  : "Ready"}
              </span>
            </div>
            {unmetBlockingClarifications.length > 0 && (
              <div className="mt-3 rounded-lg border border-[#fcd34d] bg-white/70 p-3 text-sm text-[#854d0e]">
                <div className="font-semibold">Unmet blockers</div>
                <ul className="mt-2 grid gap-1">
                  {unmetBlockingClarifications.map((clarification) => (
                    <li key={clarification.id + clarification.question}>• {clarification.question}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Info label="Must-have skills" value={mustHaveSkills.join(", ") || "-"} />
            <Info label="Geo / timezone" value={acceptableGeos.join(", ") || r.timezone_overlap || "Missing"} />
            <Info label="Budget" value={r.budget || "Missing"} />
            <Info label="Contract" value={humanize(r.contract_type)} />
          </div>
          {!!clarificationRows.length && (
            <ClarificationsPanel
              role={r}
              clarifications={clarificationRows}
            />
          )}
        </div>

        <div className="rounded-2xl border border-border bg-surface p-5">
          <h2 className="text-base font-semibold">Step 2: Search setup</h2>
          <div className="mt-4 flex flex-col gap-3">
            <Field label="Sourcing provider">
              <select
                aria-label="Sourcing provider"
                value={sourceProvider}
                onChange={(e) => onSourceProviderChange(e.target.value as SourcingProvider)}
                className={fieldInputClass()}
              >
                <option value="linkedin_xray_serp">LinkedIn X-ray via SERP (recommended)</option>
                <option value="linkedin">LinkedIn browser fallback</option>
              </select>
            </Field>
            <Field label="Search query">
              <textarea
                aria-label="Search query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={sourceProvider === "linkedin" ? '("Backend Engineer" OR "Software Engineer") AND (Python OR FastAPI)' : '"Ruby on Rails" "Vue" "Claude Code"'}
                className={fieldInputClass() + " min-h-[92px] resize-y"}
              />
            </Field>
            <Field label="Locations">
              <textarea
                aria-label="Locations"
                value={locations}
                onChange={(e) => setLocations(e.target.value)}
                placeholder={"Armenia\nGeorgia\nSerbia"}
                className={fieldInputClass() + " min-h-[76px] resize-y"}
              />
            </Field>
            {sourceProvider === "linkedin" && (
              <>
                <Field label="LinkedIn geoUrns">
                  <input
                    aria-label="LinkedIn geoUrns"
                    value={geoUrns}
                    onChange={(e) => setGeoUrns(e.target.value)}
                    placeholder="Optional: 102945703, 106057199"
                    className={fieldInputClass()}
                  />
                </Field>
                <Field label="Custom LinkedIn search URL">
                  <input
                    aria-label="Custom LinkedIn search URL"
                    value={searchUrl}
                    onChange={(e) => setSearchUrl(e.target.value)}
                    placeholder="Optional copied people-search URL"
                    className={fieldInputClass()}
                  />
                </Field>
              </>
            )}
            <Field label="Filters / notes">
              <input value={filters} onChange={(e) => setFilters(e.target.value)} className={fieldInputClass()} />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button onClick={onCreateRun} loading={createRun.isPending}>Create search run</Button>
              <Button variant="secondary" onClick={resetSearchDraft}>Use role skills</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-border bg-surface">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold">Step 3: Sourced profiles</h2>
          <p className="mt-1 text-sm text-text-muted">
            Raw sourced people stay here for evaluation, add-to-shortlist, or skip decisions only. Candidate operations start in Step 4.
          </p>
        </div>
        <div className="divide-y divide-border">
          {runs.isLoading ? (
            <EmptyState>Loading search runs...</EmptyState>
          ) : !runs.data?.items.length ? (
            <EmptyState>No search runs yet.</EmptyState>
          ) : (
            runs.data.items.map((run) => <SearchRunPanel key={run.id} run={run} />)
          )}
        </div>
      </section>

      {latestRun && (
        <details className="rounded-2xl border border-border bg-surface p-5">
          <summary className="cursor-pointer list-none text-base font-semibold">
            Manual import
          </summary>
          <p className="mt-3 text-sm text-text-muted">
            Fallback only. Use this when LinkedIn automation fails or when you want to paste a profile manually for evaluation.
          </p>
          <SourcedProfileForm run={latestRun} />
        </details>
      )}

      <section className="overflow-hidden rounded-2xl border border-border bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">Step 4: Shortlisted candidates</h2>
            <p className="mt-1 text-sm text-text-muted">
              These are the selected people for this role after review. Outreach, reply tracking, screening, hold, rejection, and submission happen here.
            </p>
          </div>
        </div>
        <div className="mt-4">
          {candidates.isLoading ? (
            <div className="text-sm text-text-muted">Loading shortlisted candidates...</div>
          ) : !candidates.data?.items.length ? (
            <div className="rounded-xl border border-dashed border-border bg-surface-alt p-4 text-sm text-text-muted">
              No shortlisted candidates yet. Use Add to shortlist on a sourced profile after evaluation.
            </div>
          ) : (
            <div className="grid gap-4">
              {groupCandidatesByLifecycle(candidates.data.items).map((group) => (
                <CandidateLifecycleSection key={group.id} group={group} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ClarificationsPanel({
  role,
  clarifications,
}: {
  role: RoleBrief;
  clarifications: ClarificationRow[];
}) {
  const blockingCount = clarifications.filter((clarification) => clarification.isBlocking && isClarificationOpen(clarification.status)).length;
  const optionalCount = clarifications.filter((clarification) => !clarification.isBlocking && isClarificationOpen(clarification.status)).length;

  return (
    <div className="mt-5 rounded-lg border border-[#fde68a] bg-warning-soft p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-[#854d0e]">Clarifications</div>
          <div className="mt-1 text-sm text-[#854d0e]">
            {blockingCount > 0
              ? `${blockingCount} blocker${blockingCount === 1 ? "" : "s"} still gate readiness.`
              : optionalCount > 0
                ? `${optionalCount} optional follow-up${optionalCount === 1 ? "" : "s"} remain open.`
                : "Every clarification has a recorded outcome."}
          </div>
        </div>
      </div>
      <div className="mt-3 grid gap-3">
        {clarifications.map((clarification) => (
          <ClarificationAnswerRow
            key={clarification.id + clarification.question}
            roleId={role.id}
            role={role}
            clarification={clarification}
          />
        ))}
      </div>
    </div>
  );
}

function ClarificationAnswerRow({
  roleId,
  role,
  clarification,
}: {
  roleId: number;
  role: RoleBrief;
  clarification: ClarificationRow;
}) {
  const answer = useAnswerRoleClarification();
  const dismiss = useDismissRoleClarification();
  const reopen = useReopenRoleClarification();
  const [draft, setDraft] = useState(clarification.answer || "");
  const [storedPatch, setStoredPatch] = useState<ClarificationPatchEntry[]>(() =>
    readClarificationPatch(roleId, clarification.id),
  );
  const canAnswer = clarification.id > 0;
  const canDismiss = clarification.id > 0 && supportsClarificationAction(clarification, "dismiss");
  const canReopen = clarification.id > 0 && supportsClarificationAction(clarification, "reopen");
  const actionPatch =
    storedPatch.length > 0
      ? storedPatch
      : buildCurrentRolePatch(role, clarification);

  useEffect(() => {
    setDraft(clarification.answer || "");
  }, [clarification.answer, clarification.id]);

  useEffect(() => {
    setStoredPatch(readClarificationPatch(roleId, clarification.id));
  }, [clarification.id, clarification.status, clarification.updated_at, roleId]);

  async function onSave() {
    if (!draft.trim()) {
      toast.error("Clarification answer is required");
      return;
    }
    if (!canAnswer) {
      toast.error("Run intake again to create clarification records");
      return;
    }
    try {
      const updatedRole = await answer.mutateAsync({
        roleBriefId: roleId,
        clarificationId: clarification.id,
        answer: draft,
      });
      const nextClarification = updatedRole.clarifications?.find((item) => item.id === clarification.id);
      const patchEntries = nextClarification ? buildPatchFromRoles(role, updatedRole, nextClarification) : [];
      writeClarificationPatch(roleId, clarification.id, patchEntries);
      setStoredPatch(patchEntries);
      toast.success("Clarification answer applied");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save clarification");
    }
  }

  async function onDismiss() {
    try {
      const updatedRole = await dismiss.mutateAsync({
        roleBriefId: roleId,
        clarificationId: clarification.id,
      });
      const nextClarification = updatedRole.clarifications?.find((item) => item.id === clarification.id);
      if (!nextClarification || nextClarification.status === "dismissed") {
        clearClarificationPatch(roleId, clarification.id);
        setStoredPatch([]);
      }
      toast.success("Clarification dismissed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not dismiss clarification");
    }
  }

  async function onReopen() {
    try {
      await reopen.mutateAsync({
        roleBriefId: roleId,
        clarificationId: clarification.id,
      });
      toast.success("Clarification reopened");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not reopen clarification");
    }
  }

  return (
    <div className="rounded-lg border border-[#fde68a] bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-text">{clarification.question}</div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
            <span className={"inline-flex items-center rounded-full px-2 py-0.5 " + (clarification.isBlocking
              ? "bg-[#fef3c7] text-[#92400e]"
              : "bg-surface-alt text-text-muted")}>
              {clarification.isBlocking ? "Blocking" : "Optional"}
            </span>
            {clarification.status === "applied" && (
              <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-[#166534]">
                Applied to role brief
              </span>
            )}
            {clarification.status === "answered" && (
              <span className="inline-flex items-center rounded-full bg-surface-alt px-2 py-0.5 text-text-muted">
                Audit-only answer
              </span>
            )}
          </div>
        </div>
        <StatusTag status={clarification.status} />
      </div>
      <div className="mt-3 grid gap-2">
        <div className="rounded-md border border-border bg-surface-alt px-3 py-2 text-sm">
          <div className="font-semibold text-text">Why this matters</div>
          <div className="mt-1 text-text-muted">{clarification.whyMatters}</div>
        </div>
        <Field label="Admin answer">
          <textarea
            aria-label={"Answer clarification: " + clarification.question}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={fieldInputClass() + " min-h-[72px] resize-y"}
          />
        </Field>
        {clarification.applied_to_field === "clarification_audit" && (
          <div className="rounded-md border border-border bg-surface-alt px-3 py-2 text-xs text-text-muted">
            Answer is stored in the clarification audit trail. No role field changed.
          </div>
        )}
        {!!actionPatch.length && clarification.applied_to_field !== "clarification_audit" && (
          <div className="rounded-md border border-border bg-surface-alt px-3 py-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Applied field patch</div>
            <div className="mt-2 grid gap-2">
              {actionPatch.map((entry) => (
                <div key={entry.field} className="grid gap-1 text-sm sm:grid-cols-[150px_minmax(0,1fr)]">
                  <div className="font-mono text-xs text-text-muted">{entry.field}</div>
                  <div className="break-words text-text">
                    <span className="text-text-muted">{entry.before}</span>
                    <span className="px-2 text-text-muted">→</span>
                    <span>{entry.after}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {clarification.applied_summary && (
          <div className="text-xs text-text-muted">
            {clarification.applied_summary}
          </div>
        )}
        {clarification.answered_at && (
          <div className="text-xs text-text-muted">
            Answered {fmtDate(clarification.answered_at)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={onSave} loading={answer.isPending} disabled={!canAnswer}>
            Save and apply answer
          </Button>
          {canDismiss && (
            <Button size="sm" variant="ghost" onClick={onDismiss} loading={dismiss.isPending}>
              Dismiss
            </Button>
          )}
          {canReopen && (
            <Button size="sm" variant="secondary" onClick={onReopen} loading={reopen.isPending}>
              Reopen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function deriveClarificationRows(
  role: RoleBrief,
  clarifications: RoleClarification[],
  missingQuestions: string[],
): ClarificationRow[] {
  const rows = clarifications.length
    ? clarifications
    : missingQuestions.map((question, index) => ({
        id: -index - 1,
        role_brief_id: role.id,
        question,
        answer: null,
        status: "open",
        applied_to_field: null,
        applied_summary: null,
        blocking: null,
        why_it_matters: null,
        available_actions: null,
        field_patch: null,
        dismissed_at: null,
        dismissed_by_admin_id: null,
        answered_by_admin_id: null,
        answered_at: null,
        created_at: role.created_at,
        updated_at: role.updated_at,
      }));

  return rows
    .map((clarification) => ({
      ...clarification,
      isBlocking: isClarificationBlocking(clarification, missingQuestions),
      whyMatters: clarification.why_it_matters || defaultClarificationReason(clarification),
      targetFields: clarificationTargetFields(clarification),
    }))
    .sort((left, right) => {
      const leftOpen = isClarificationOpen(left.status) ? 0 : 1;
      const rightOpen = isClarificationOpen(right.status) ? 0 : 1;
      if (leftOpen !== rightOpen) return leftOpen - rightOpen;
      if (left.isBlocking !== right.isBlocking) return left.isBlocking ? -1 : 1;
      return left.id - right.id;
    });
}

function supportsClarificationAction(
  clarification: Pick<RoleClarification, "available_actions">,
  action: ClarificationAction,
): boolean {
  return clarification.available_actions?.includes(action) ?? false;
}

function isClarificationOpen(status: string): boolean {
  return !["answered", "applied", "dismissed"].includes(status);
}

function isClarificationBlocking(
  clarification: Pick<RoleClarification, "blocking" | "question" | "applied_to_field" | "field_patch">,
  missingQuestions: string[],
): boolean {
  if (typeof clarification.blocking === "boolean") return clarification.blocking;
  const targets = clarificationTargetFields(clarification);
  if (targets.some((field) => BLOCKING_CLARIFICATION_FIELDS.has(field))) return true;
  if (targets.some((field) => OPTIONAL_CLARIFICATION_FIELDS.has(field))) return false;
  return missingQuestions.includes(clarification.question);
}

function clarificationTargetFields(
  clarification: Pick<RoleClarification, "question" | "applied_to_field" | "field_patch">,
): string[] {
  const fromPatch = clarification.field_patch?.map((entry) => entry.field).filter(Boolean) ?? [];
  if (fromPatch.length) return Array.from(new Set(fromPatch));
  if (clarification.applied_to_field) {
    return clarification.applied_to_field
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }
  const derived = deriveClarificationTarget(clarification.question);
  return derived ? [derived] : [];
}

function deriveClarificationTarget(question: string): string | null {
  const lower = question.toLowerCase();
  if (lower.includes("geographies") || lower.includes("timezones")) return "acceptable_geos";
  if (lower.includes("budget") || lower.includes("rate") || lower.includes("compensation")) return "budget";
  if (lower.includes("skills") || lower.includes("must-haves")) return "must_have_skills";
  if (lower.includes("start")) return "start_date";
  if (lower.includes("client/company name") || lower.includes("mention the client")) return "client_name_share_allowed";
  return null;
}

function defaultClarificationReason(
  clarification: Pick<RoleClarification, "question" | "applied_to_field" | "field_patch">,
): string {
  const targets = clarificationTargetFields(clarification);
  if (targets.includes("acceptable_geos") || targets.includes("timezone_overlap")) {
    return "Needed before search filters and geo-fit decisions are trustworthy.";
  }
  if (targets.includes("budget")) {
    return "Needed before shortlist decisions so rate-fit is explicit and auditable.";
  }
  if (targets.includes("must_have_skills")) {
    return "Needed before query generation and evaluation so the rail searches for the right profile.";
  }
  if (targets.includes("start_date")) {
    return "Useful for urgency and availability planning, but it should not block sourcing by itself.";
  }
  if (targets.includes("client_name_share_allowed")) {
    return "Needed for outreach wording and auditability when deciding whether the client name can be shared.";
  }
  return "Keeps the intake decision trace auditable even when no direct role field changes are applied.";
}

function buildPatchFromRoles(
  beforeRole: RoleBrief,
  afterRole: RoleBrief,
  clarification: Pick<RoleClarification, "question" | "applied_to_field" | "field_patch">,
): ClarificationPatchEntry[] {
  if (clarification.field_patch?.length) {
    return clarification.field_patch.map((entry) => ({
      field: entry.field,
      before: serializeClarificationValue(entry.before),
      after: serializeClarificationValue(entry.after),
    }));
  }
  return clarificationTargetFields(clarification)
    .filter((field) => field !== "clarification_audit")
    .map((field) => ({
      field,
      before: formatRoleFieldValue(beforeRole, field),
      after: formatRoleFieldValue(afterRole, field),
    }));
}

function buildCurrentRolePatch(
  role: RoleBrief,
  clarification: Pick<RoleClarification, "status" | "question" | "applied_to_field" | "field_patch">,
): ClarificationPatchEntry[] {
  if (clarification.status !== "applied") return [];
  return clarificationTargetFields(clarification)
    .filter((field) => field !== "clarification_audit")
    .map((field) => ({
      field,
      before: "Previous value unavailable",
      after: formatRoleFieldValue(role, field),
    }));
}

function formatRoleFieldValue(role: RoleBrief, field: string): string {
  switch (field) {
    case "acceptable_geos":
      return role.acceptable_geos?.join(", ") || "Missing";
    case "timezone_overlap":
      return role.timezone_overlap || "Missing";
    case "budget":
      return role.budget || "Missing";
    case "must_have_skills":
      return role.must_have_skills?.join(", ") || "Missing";
    case "start_date":
      return role.start_date || "Missing";
    case "client_name_share_allowed":
      return humanize(role.client_name_share_allowed);
    default:
      return serializeClarificationValue((role as unknown as Record<string, unknown>)[field]);
  }
}

function serializeClarificationValue(value: unknown): string {
  if (value == null) return "Missing";
  if (Array.isArray(value)) return value.join(", ") || "Missing";
  if (typeof value === "object") return JSON.stringify(value);
  const text = String(value).trim();
  return text || "Missing";
}

function clarificationPatchStorageKey(roleId: number, clarificationId: number): string {
  return `${CLARIFICATION_PATCH_STORAGE_PREFIX}:${roleId}:${clarificationId}`;
}

function readClarificationPatch(roleId: number, clarificationId: number): ClarificationPatchEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(clarificationPatchStorageKey(roleId, clarificationId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeClarificationPatch(roleId: number, clarificationId: number, patch: ClarificationPatchEntry[]) {
  if (typeof window === "undefined" || !patch.length) return;
  window.sessionStorage.setItem(clarificationPatchStorageKey(roleId, clarificationId), JSON.stringify(patch));
}

function clearClarificationPatch(roleId: number, clarificationId: number) {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(clarificationPatchStorageKey(roleId, clarificationId));
}

function SearchRunPanel({ run }: { run: SearchRun }) {
  const profiles = useSourcedProfiles(run.id);
  const tasks = useSourcingTasks(run.id);
  const sourceOne = useSourceOneProfile();
  const cancelTask = useCancelSourcingTask();
  const retryTask = useRetrySourcingTask();

  async function onSourceOne() {
    try {
      if (latestTask && ["failed", "cancelled"].includes(latestTask.status)) {
        await retryTask.mutateAsync({
          taskId: latestTask.id,
          searchRunId: run.id,
        });
        toast.success("Source-one retry queued");
      } else {
        await sourceOne.mutateAsync({
          searchRunId: run.id,
          body: {
            instructions: "Use shared LinkedIn browser. Find exactly one profile, evaluate it, then stop for admin decision.",
          },
        });
        toast.success("Source-one task queued");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not queue sourcing task");
    }
  }

  async function onCancelTask(task: SourcingTask) {
    try {
      await cancelTask.mutateAsync({ taskId: task.id, searchRunId: run.id });
      toast.success("Sourcing task cancelled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not cancel sourcing task");
    }
  }

  const activeTask = tasks.data?.items.find((task) => ["queued", "running"].includes(task.status));
  const latestTask = tasks.data?.items[0] ?? null;
  const taskNotice = latestTask ? sourcingTaskNotice(latestTask) : null;
  const latestTaskObservability = latestTask ? readTaskObservability(latestTask) : null;
  const canRetryLatestTask = latestTask != null && ["failed", "cancelled"].includes(latestTask.status);
  const sourceButtonLabel = canRetryLatestTask ? "Retry last attempt" : "Source one profile";

  return (
    <div className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="break-words font-semibold">{run.source}: {run.search_query}</div>
          <div className="mt-1 text-sm text-text-muted">{run.status} · {fmtDate(run.created_at)}</div>
          {renderSearchFilters(run)}
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          {activeTask?.status === "queued" && (
            <Button
              variant="danger"
              size="sm"
              className="w-full whitespace-normal sm:w-auto"
              onClick={() => onCancelTask(activeTask)}
              loading={cancelTask.isPending}
            >
              Cancel queued task
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="w-full whitespace-normal sm:w-auto"
            onClick={onSourceOne}
            loading={sourceOne.isPending || retryTask.isPending}
            disabled={!!activeTask}
          >
            {sourceButtonLabel}
          </Button>
        </div>
      </div>
      {taskNotice && (
        <div className={"mt-3 rounded-lg border px-3 py-2 text-sm " + taskNotice.className}>
          <div className="font-semibold">{taskNotice.title}</div>
          <div className="mt-1 break-words">{taskNotice.message}</div>
          {activeTask?.status === "running" && (
            <div className="mt-1 text-xs">
              Running tasks cannot be cancelled safely yet; retry becomes available after the worker finishes or fails.
            </div>
          )}
          {latestTaskObservability && hasTaskObservability(latestTaskObservability) && (
            <TaskObservabilityGrid observability={latestTaskObservability} compact />
          )}
          {canRetryLatestTask && !activeTask && (
            <div className="mt-1 text-xs">
              This attempt can be retried safely. The retry stays approval-gated and will not send LinkedIn actions.
            </div>
          )}
        </div>
      )}
      {!!tasks.data?.items.length && (
        <TaskLog tasks={tasks.data.items} />
      )}
      <div className="mt-4 grid gap-3">
        {profiles.isLoading ? (
          <div className="text-sm text-text-muted">Loading profiles...</div>
        ) : !profiles.data?.items.length ? (
          <div className="text-sm text-text-muted">No sourced profiles yet.</div>
        ) : (
          profiles.data.items.map((profile) => <ProfileCard key={profile.id} profile={profile} />)
        )}
      </div>
    </div>
  );
}

function TaskLog({ tasks }: { tasks: SourcingTask[] }) {
  return (
    <details className="mt-3 rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm">
      <summary className="cursor-pointer list-none font-semibold">Task log ({tasks.length})</summary>
      <div className="mt-3 grid gap-2">
        {tasks.slice(0, 5).map((task) => {
          const observability = readTaskObservability(task);
          return (
            <div key={task.id} className="rounded-md border border-border bg-surface p-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusTag status={task.status_reason || task.status} />
                <span className="font-medium">{task.status_label || humanize(task.status_reason || task.status)}</span>
                <span className="text-text-muted">#{task.id}</span>
                <span className="text-text-muted">{task.agent_name}</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <Info label="Queued" value={fmtDate(task.created_at)} />
                <Info label="Started" value={task.started_at ? fmtDate(task.started_at) : "Not started"} />
                <Info label="Finished" value={task.completed_at ? fmtDate(task.completed_at) : "Not finished"} />
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Info label="Task state" value={task.status_label || humanize(task.status_reason || task.status)} />
                <Info label="Safe admin control" value={safeTaskControlLabel(task)} />
              </div>
              {hasTaskObservability(observability) && (
                <TaskObservabilityGrid observability={observability} />
              )}
              {(task.result_summary || task.error_message || task.instructions) && (
                <div className="mt-2 space-y-1 break-words text-text-muted">
                  {task.result_summary && <div>Result: {task.result_summary}</div>}
                  {task.error_message && <div>Error: {task.error_message}</div>}
                  {task.instructions && <div>Instructions: {task.instructions}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}

type TaskObservability = {
  attempts: string | null;
  cooldown: string | null;
  cooldownReason: string | null;
  workerLogHref: string | null;
  workerLogLabel: string | null;
  workerLogPath: string | null;
};

function TaskObservabilityGrid({
  observability,
  compact = false,
}: {
  observability: TaskObservability;
  compact?: boolean;
}) {
  if (!hasTaskObservability(observability)) return null;
  const textSize = compact ? "text-xs" : "text-sm";

  return (
    <div className={"mt-2 grid gap-2 sm:grid-cols-3 " + textSize}>
      {observability.attempts && (
        <Info label="Attempts" value={observability.attempts} />
      )}
      {(observability.cooldown || observability.cooldownReason) && (
        <Info
          label="Cooldown"
          value={
            <div className="break-words">
              {observability.cooldown && <div>{observability.cooldown}</div>}
              {observability.cooldownReason && (
                <div className="text-text-muted">{observability.cooldownReason}</div>
              )}
            </div>
          }
        />
      )}
      {(observability.workerLogHref || observability.workerLogPath) && (
        <Info
          label="Worker log"
          value={
            <div className="break-words">
              {observability.workerLogHref ? (
                <a
                  href={observability.workerLogHref}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {observability.workerLogLabel || "Open worker log"}
                </a>
              ) : (
                <div>{observability.workerLogLabel || "Latest worker log"}</div>
              )}
              {observability.workerLogPath && (
                <div className="text-text-muted">{observability.workerLogPath}</div>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}

function SourcedProfileForm({ run }: { run: SearchRun }) {
  const createProfile = useCreateSourcedProfile();
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [location, setLocation] = useState("");
  const [raw, setRaw] = useState("");

  async function onSubmit() {
    if (!url.trim()) {
      toast.error("LinkedIn URL is required");
      return;
    }
    try {
      await createProfile.mutateAsync({
        searchRunId: run.id,
        body: {
          source: "linkedin",
          profile_url: url,
          public_name: name || null,
          headline: headline || null,
          location: location || null,
          raw_text: raw || null,
        },
      });
      setUrl("");
      setName("");
      setHeadline("");
      setLocation("");
      setRaw("");
      toast.success("Sourced profile added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not add profile");
    }
  }

  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <Field label="LinkedIn URL" className="sm:col-span-2">
        <input aria-label="LinkedIn URL" value={url} onChange={(e) => setUrl(e.target.value)} className={fieldInputClass()} />
      </Field>
      <Field label="Name">
        <input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} className={fieldInputClass()} />
      </Field>
      <Field label="Location">
        <input aria-label="Location" value={location} onChange={(e) => setLocation(e.target.value)} className={fieldInputClass()} />
      </Field>
      <Field label="Headline" className="sm:col-span-2">
        <input aria-label="Headline" value={headline} onChange={(e) => setHeadline(e.target.value)} className={fieldInputClass()} />
      </Field>
      <Field label="Raw extracted text" className="sm:col-span-2">
        <textarea aria-label="Raw extracted text" value={raw} onChange={(e) => setRaw(e.target.value)} className={fieldInputClass() + " min-h-[90px] resize-y"} />
      </Field>
      <div className="sm:col-span-2">
        <Button onClick={onSubmit} loading={createProfile.isPending}>Add sourced profile</Button>
      </div>
    </div>
  );
}

function ProfileCard({ profile }: { profile: SourcedProfile }) {
  const aiEvaluate = useAiEvaluateSourcedProfile();
  const skipProfile = useSkipSourcedProfile();
  const promote = usePromoteSourcedProfile();

  const evaluation = profile.latest_evaluation ?? null;
  const [candidateId, setCandidateId] = useState<number | null>(profile.duplicate_candidate_id ?? null);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  async function onEvaluate() {
    try {
      await aiEvaluate.mutateAsync({
        profileId: profile.id,
        searchRunId: profile.search_run_id,
      });
      toast.success("AI evaluation saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "AI evaluation failed");
    }
  }

  async function onPromote() {
    try {
      const candidate = await promote.mutateAsync({
        profileId: profile.id,
        body: {
          core_stack: [],
          consent_status: "unknown",
          representation_status: "shortlist_ready",
          notes: "Added to shortlist from sourced profile after role evaluation.",
        },
      });
      setCandidateId(candidate.id);
      toast.success("Added to shortlist");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Promotion failed");
    }
  }

  async function onSkip() {
    try {
      await skipProfile.mutateAsync({
        profileId: profile.id,
        searchRunId: profile.search_run_id,
      });
      setDismissed(true);
      toast.success("Profile skipped");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not skip profile");
    }
  }

  return (
    <article
      aria-label={`Sourced profile ${profile.public_name || profile.profile_url}`}
      className="rounded-xl border border-border bg-surface-alt p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="break-words font-semibold">{profile.public_name || profile.profile_url}</div>
          <div className="break-words mt-1 text-sm text-text-muted">{profile.headline || "No headline"} · {profile.location || "Location missing"}</div>
          <SafeExternalLink href={profile.profile_url} label="LinkedIn profile" />
        </div>
        <StatusTag status={profile.status} />
      </div>

      <div className="mt-4 rounded-lg border border-border bg-surface p-3">
        <div className="grid gap-3 sm:grid-cols-[160px_170px_minmax(0,1fr)]">
          <Info label="AI evaluation score" value={evaluation ? evaluation.score + "/100" : "Not evaluated"} />
          <Info label="Recommendation" value={evaluation ? humanize(evaluation.recommendation) : "Run evaluation"} />
          <Info label="Evaluator" value={evaluation?.evaluator_version || "linkedin-sourcing-evaluator-v1"} />
        </div>
        {evaluation && (
          <div className="mt-3 flex flex-wrap gap-2">
            <FitPill label="Geo" value={evaluation.geo_fit} />
            <FitPill label="Seniority" value={evaluation.seniority_fit} />
            <FitPill label="Stack" value={evaluation.stack_fit} />
            <FitPill label="AI" value={evaluation.ai_fit} />
          </div>
        )}
        {evaluation && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <InfoList label="Evidence" values={evaluation.evidence} empty="No strong evidence extracted yet" />
            <InfoList label="Gaps / risks" values={evaluation.gaps.map(humanizeGap)} empty="No major gaps detected" />
          </div>
        )}
      </div>

      <div className="mt-3 grid gap-2 sm:flex sm:flex-wrap">
        <Button variant="secondary" onClick={onEvaluate} loading={aiEvaluate.isPending} className="w-full whitespace-normal sm:w-auto">
          {evaluation ? "Re-run evaluation" : "Run AI evaluation"}
        </Button>
        <Button
          variant="secondary"
          onClick={onPromote}
          loading={promote.isPending}
          disabled={!evaluation || !!candidateId || evaluation.recommendation === "skip"}
          className="w-full whitespace-normal sm:w-auto"
        >
          {candidateId ? "Already shortlisted" : "Add to shortlist"}
        </Button>
        <Button variant="danger" onClick={onSkip} loading={skipProfile.isPending} className="w-full whitespace-normal sm:w-auto">Skip profile</Button>
      </div>
    </article>
  );
}

type CandidateLifecycleStage = {
  id: string;
  label: string;
  description: string;
  statuses: string[];
  primaryStatus: string;
};

type CandidateLifecycleGroup = CandidateLifecycleStage & {
  candidates: Candidate[];
};

type RoleRailCandidateStatus = CandidateRepresentationStatus;

const CANDIDATE_LIFECYCLE_STAGES: CandidateLifecycleStage[] = [
  {
    id: "shortlist",
    label: "Shortlist ready",
    description: "Selected for this role and waiting for outreach or a next decision.",
    statuses: ["shortlist_ready", "not_contacted"],
    primaryStatus: "shortlist_ready",
  },
  {
    id: "outreach",
    label: "Outreach pending",
    description: "Outreach approval exists or outreach is queued, but nothing has been sent yet.",
    statuses: ["outreach_pending"],
    primaryStatus: "outreach_pending",
  },
  {
    id: "contacted",
    label: "Contacted",
    description: "Outreach has been sent or queued for follow-up.",
    statuses: ["contacted"],
    primaryStatus: "contacted",
  },
  {
    id: "replied",
    label: "Replied",
    description: "Candidate responded and needs qualification or scheduling.",
    statuses: ["replied"],
    primaryStatus: "replied",
  },
  {
    id: "screening",
    label: "Screening",
    description: "Candidate is in recruiter screening for this role.",
    statuses: ["screening"],
    primaryStatus: "screening",
  },
  {
    id: "submitted",
    label: "Submitted",
    description: "Candidate has been sent to the client for review.",
    statuses: ["submitted", "represented"],
    primaryStatus: "submitted",
  },
  {
    id: "hold",
    label: "Hold",
    description: "Paused without losing the candidate from the role shortlist.",
    statuses: ["hold"],
    primaryStatus: "hold",
  },
  {
    id: "rejected",
    label: "Rejected",
    description: "No longer moving forward for this role.",
    statuses: ["rejected", "archived"],
    primaryStatus: "rejected",
  },
  {
    id: "placed",
    label: "Placed",
    description: "Candidate has reached a final successful outcome.",
    statuses: ["placed"],
    primaryStatus: "placed",
  },
];

const CANDIDATE_STATUS_ACTIONS: Array<{
  value: RoleRailCandidateStatus;
  label: string;
  variant?: "secondary" | "danger" | "success";
}> = [
  { value: "outreach_pending", label: "Outreach pending", variant: "secondary" },
  { value: "contacted", label: "Contacted", variant: "secondary" },
  { value: "replied", label: "Replied", variant: "secondary" },
  { value: "screening", label: "Screening", variant: "secondary" },
  { value: "submitted", label: "Submitted", variant: "success" },
  { value: "hold", label: "Hold", variant: "secondary" },
  { value: "rejected", label: "Reject", variant: "danger" },
];

const CANDIDATE_ROLE_LIFECYCLE_ACTIONS: Array<{
  value: RoleRailCandidateStatus;
  label: string;
  description: string;
  variant?: "secondary" | "danger" | "success";
}> = [
  {
    value: "shortlist_ready",
    label: "Back to shortlist",
    description: "Selected, not actively contacted yet.",
    variant: "secondary",
  },
  {
    value: "outreach_pending",
    label: "Queue outreach",
    description: "Approval exists or outreach is queued.",
    variant: "secondary",
  },
  {
    value: "contacted",
    label: "Mark outreach sent",
    description: "First role-specific outreach is done.",
    variant: "secondary",
  },
  {
    value: "replied",
    label: "Mark replied",
    description: "Candidate answered and needs follow-up.",
    variant: "secondary",
  },
  {
    value: "screening",
    label: "Move to screening",
    description: "Recruiter qualification is underway.",
    variant: "secondary",
  },
  {
    value: "submitted",
    label: "Submit to client",
    description: "Ready for client review.",
    variant: "success",
  },
  {
    value: "placed",
    label: "Mark placed",
    description: "Successful role outcome.",
    variant: "success",
  },
  {
    value: "hold",
    label: "Put on hold",
    description: "Pause without losing the shortlist link.",
    variant: "secondary",
  },
  {
    value: "rejected",
    label: "Reject for role",
    description: "Stop this role-specific candidacy.",
    variant: "danger",
  },
];

function CandidateLifecycleSection({ group }: { group: CandidateLifecycleGroup }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{group.label}</h3>
            <StatusTag status={group.primaryStatus} label={group.label} />
          </div>
          <p className="mt-1 break-words text-xs text-text-muted">{group.description}</p>
        </div>
        <div className="rounded-full bg-surface-alt px-2.5 py-1 text-xs font-semibold text-text-muted">
          {group.candidates.length}
        </div>
      </div>
      <div className="grid gap-3 p-3">
        {group.candidates.map((candidate) => (
          <CandidateCard key={candidate.id} candidate={candidate} />
        ))}
      </div>
    </section>
  );
}

function CandidateCard({ candidate }: { candidate: Candidate }) {
  const outreach = useCreateOutreachApproval();
  const update = useUpdateCandidate();
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState(candidate.representation_status);
  const [pendingStatus, setPendingStatus] = useState<RoleRailCandidateStatus | null>(null);

  useEffect(() => {
    setStatus(candidate.representation_status);
  }, [candidate.id, candidate.representation_status]);

  async function onCreateApproval() {
    if (!draft.trim()) {
      toast.error("Draft text is required");
      return;
    }
    try {
      await outreach.mutateAsync({
        candidate_id: candidate.id,
        role_brief_id: candidate.role_brief_id ?? null,
        channel: "linkedin_connect",
        draft_text: draft,
        risk_notes: "Exact text must be approved before sending.",
      });
      if (status === "shortlist_ready" || status === "not_contacted") {
        await onStatusChange("outreach_pending");
      }
      setDraft("");
      toast.success("Outreach approval created");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Approval failed");
    }
  }

  async function onStatusChange(nextStatus: RoleRailCandidateStatus) {
    if (nextStatus === status) return;
    setPendingStatus(nextStatus);
    try {
      await update.mutateAsync({
        id: candidate.id,
        body: { representation_status: nextStatus },
      });
      setStatus(nextStatus);
      toast.success("Candidate moved to " + lifecycleStatusLabel(nextStatus));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Status update failed");
    } finally {
      setPendingStatus(null);
    }
  }

  return (
    <article
      aria-label={`Shortlisted candidate ${candidate.name}`}
      className="min-w-0 overflow-hidden rounded-lg border border-border bg-surface-alt p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="break-words font-semibold">{candidate.name}</div>
          <div className="mt-1 break-words text-sm text-text-muted">
            {candidate.current_title || "Current title missing"} · {candidate.location || candidate.geo || "Location missing"}
          </div>
          {candidate.linkedin_url && <SafeExternalLink href={candidate.linkedin_url} label="LinkedIn profile" />}
        </div>
        <StatusTag status={status} label={lifecycleStatusLabel(status)} />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Info label="Consent" value={humanize(candidate.consent_status)} />
        <Info label="Pipeline status" value={lifecycleStatusLabel(status)} />
        <Info label="Expected rate" value={candidate.expected_rate || "Not set"} />
        <Info label="Availability" value={candidate.availability || "Not set"} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Info label="Stack" value={candidate.core_stack.join(", ") || "Not set"} />
        <Info label="Notes" value={candidate.notes || "No notes yet"} />
      </div>
      <div className="mt-4 border-t border-border pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">Role lifecycle</div>
            <p className="mt-1 text-xs text-text-muted">
              These controls update this candidate's status for this role shortlist.
            </p>
          </div>
          <StatusTag status={status} label={lifecycleStatusLabel(status)} />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {CANDIDATE_ROLE_LIFECYCLE_ACTIONS.map((action) => {
            const isCurrent = status === action.value;
            return (
              <Button
                key={action.value}
                type="button"
                variant={action.variant ?? "secondary"}
                size="sm"
                className="min-h-[58px] w-full min-w-0 flex-col items-start whitespace-normal px-3 py-2 text-left leading-snug"
                aria-pressed={isCurrent}
                onClick={() => onStatusChange(action.value)}
                loading={pendingStatus === action.value}
                disabled={update.isPending || isCurrent}
              >
                <span className="w-full break-words">{action.label}</span>
                <span className="w-full break-words text-[11px] font-medium opacity-75">
                  {isCurrent ? "Current status" : action.description}
                </span>
              </Button>
            );
          })}
        </div>
      </div>
      <div className="mt-4 grid gap-3 border-t border-border pt-4">
        <Field label="LinkedIn outreach draft">
          <textarea
            aria-label={`LinkedIn outreach draft for ${candidate.name}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={fieldInputClass() + " min-h-[80px] resize-y"}
          />
        </Field>
        <div className="grid gap-2 sm:flex sm:flex-wrap">
          <Button onClick={onCreateApproval} loading={outreach.isPending} className="w-full sm:w-auto">
            Create outreach approval
          </Button>
          <Link to={"/admin/candidates/" + candidate.id} className="inline-flex w-full items-center justify-center rounded-md border border-border px-3 py-2 text-center text-sm font-medium sm:w-auto">
            Open candidate
          </Link>
        </div>
      </div>
    </article>
  );
}

function groupCandidatesByLifecycle(candidates: Candidate[]): CandidateLifecycleGroup[] {
  const matchedIds = new Set<number>();
  const groups = CANDIDATE_LIFECYCLE_STAGES.map((stage) => {
    const stageCandidates = candidates.filter((candidate) =>
      stage.statuses.includes(candidate.representation_status),
    );
    stageCandidates.forEach((candidate) => matchedIds.add(candidate.id));
    return { ...stage, candidates: stageCandidates };
  }).filter((group) => group.candidates.length > 0);

  const otherCandidates = candidates.filter((candidate) => !matchedIds.has(candidate.id));
  if (otherCandidates.length) {
    groups.push({
      id: "other",
      label: "Other status",
      description: "Candidates with a lifecycle value not mapped by the role rail yet.",
      statuses: [],
      primaryStatus: "unknown",
      candidates: otherCandidates,
    });
  }
  return groups;
}

function lifecycleStatusLabel(status: string): string {
  const action = CANDIDATE_STATUS_ACTIONS.find((item) => item.value === status);
  if (action) return action.value === "rejected" ? "Rejected" : action.label;
  if (status === "outreach_pending") return "Outreach pending";
  if (status === "shortlist_ready") return "Shortlist ready";
  if (status === "not_contacted") return "Not contacted";
  if (status === "represented") return "Represented";
  if (status === "placed") return "Placed";
  if (status === "archived") return "Archived";
  return humanize(status);
}

function Info({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}

function InfoList({ label, values, empty }: { label: string; values: string[]; empty: string }) {
  const cleanValues = values.map((value) => value.trim()).filter(Boolean);
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      {cleanValues.length ? (
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
          {cleanValues.map((value) => <li key={value}>{value}</li>)}
        </ul>
      ) : (
        <div className="mt-1 text-sm text-text-muted">{empty}</div>
      )}
    </div>
  );
}

function FitPill({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  const low = value.toLowerCase();
  const tone =
    low.includes("good") || low.includes("strong")
      ? "border-[#bbf7d0] bg-success-soft text-success"
      : low.includes("weak") || low.includes("mismatch") || low.includes("excluded")
        ? "border-[#fecaca] bg-danger-soft text-danger"
        : "border-border bg-surface-alt text-text-muted";
  return (
    <span className={"rounded-full border px-2.5 py-1 text-xs font-medium " + tone}>
      {label}: {humanize(low)}
    </span>
  );
}

function EmptyState({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "danger";
}) {
  return (
    <div
      className={
        "rounded-2xl border bg-surface p-6 text-center text-sm " +
        (tone === "danger" ? "border-[#fecaca] text-danger" : "border-border text-text-muted")
      }
    >
      {children}
    </div>
  );
}

function SafeExternalLink({ href, label }: { href?: string | null; label: string }) {
  const safe = safeUrl(href);
  if (!safe) return <div className="mt-1 break-all text-sm text-text-muted">{href || "No external URL"}</div>;
  return (
    <a href={safe} target="_blank" rel="noreferrer" className="mt-1 inline-block break-all text-sm font-medium text-primary">
      {label}
    </a>
  );
}

function splitLines(value: string): string[] {
  return value.split(/[;,\n]/).map((item) => item.trim()).filter(Boolean);
}

function defaultSearchQuery(role: Pick<RoleBrief, "seniority" | "role_title" | "must_have_skills">): string {
  const title = role.role_title.trim();
  const seniority = role.seniority.trim();
  return [
    linkedinOrGroup(titleQueryTerms(title)),
    linkedinOrGroup(seniorityQueryTerms(seniority, title)),
    ...role.must_have_skills.slice(0, 4).map((skill) => linkedinOrGroup(skillQueryTerms(skill))),
  ]
    .filter(Boolean)
    .join(" AND ");
}

function browserFallbackSearchQuery(role: Pick<RoleBrief, "role_title" | "must_have_skills">): string {
  const titleGroup = linkedinOrGroup([...titleQueryTerms(role.role_title), ...adjacentTitleTerms(role.role_title, role.must_have_skills)]);
  const skillGroup = linkedinOrGroup(role.must_have_skills.slice(0, 5).flatMap((skill) => skillQueryTerms(skill).slice(0, 2)));
  return [titleGroup, skillGroup].filter(Boolean).join(" AND ");
}

function linkedinQueryToken(value: string): string {
  const cleaned = value.replace(/["'()[\]{}]/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  return /\s/.test(cleaned) ? '"' + cleaned + '"' : cleaned;
}

function linkedinOrGroup(terms: string[]): string {
  const cleaned = terms.map(linkedinQueryToken).filter(Boolean);
  const deduped = cleaned.filter((term, index) => cleaned.findIndex((item) => item.toLowerCase() === term.toLowerCase()) === index);
  if (deduped.length <= 1) return deduped[0] ?? "";
  return "(" + deduped.join(" OR ") + ")";
}

function titleQueryTerms(title: string): string[] {
  const generic = title
    .replace(/\b(junior|jr\.?|middle|mid(?:-level)?|senior|sr\.?|lead|principal|staff)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return [title, generic].filter(Boolean);
}

function seniorityQueryTerms(seniority: string, title: string): string[] {
  const low = (seniority + " " + title).toLowerCase();
  if (/\b(junior|jr\.?)\b/.test(low)) return ["junior", "jr"];
  if (/\b(middle|mid(?:-level)?|intermediate)\b/.test(low)) return ["middle", "mid-level", "intermediate"];
  if (/\b(lead|principal|staff|architect)\b/.test(low)) return ["lead", "principal", "staff", "architect"];
  if (/\b(senior|sr\.?)\b/.test(low)) return ["senior", "sr", "lead", "principal", "staff"];
  return seniority ? [seniority] : [];
}

function skillQueryTerms(skill: string): string[] {
  const normalized = skill.toLowerCase().replace(/[^a-z0-9+#.]+/g, " ").trim();
  const aliases: Record<string, string[]> = {
    "api testing": ["API testing", "Postman", "REST API"],
    "ai automation": ["AI automation", "AI agents", "LLM", "automation"],
    fastapi: ["FastAPI", "Python"],
    "llm agents": ["LLM agents", "AI agents", "Claude", "Cursor", "MCP"],
    "manual qa": ["Manual QA", "QA testing", "Quality Assurance"],
    "openai api": ["OpenAI API", "OpenAI", "LLM"],
    postgresql: ["PostgreSQL", "Postgres"],
    "ruby on rails": ["Ruby on Rails", "Rails"],
    "vue 3": ["Vue 3", "Vue.js", "Vue"],
  };
  return aliases[normalized] ?? [skill];
}

function adjacentTitleTerms(title: string, skills: string[]): string[] {
  const lowTitle = title.toLowerCase();
  const lowSkills = skills.join(" ").toLowerCase();
  const terms: string[] = [];
  if (/python|django|fastapi/.test(lowTitle + " " + lowSkills)) terms.push("Python Developer", "Backend Engineer", "Software Engineer");
  if (/ruby|rails/.test(lowTitle + " " + lowSkills)) terms.push("Ruby Developer", "Rails Developer", "Backend Engineer");
  if (/react|vue|angular|frontend|front-end/.test(lowTitle + " " + lowSkills)) terms.push("Frontend Developer", "Front End Developer", "Software Engineer");
  if (/qa|test|quality/.test(lowTitle + " " + lowSkills)) terms.push("QA Engineer", "Test Engineer", "Quality Assurance Engineer");
  if (/devops|aws|cloud|kubernetes/.test(lowTitle + " " + lowSkills)) terms.push("DevOps Engineer", "Cloud Engineer", "Platform Engineer");
  return terms;
}

function skillTokens(skill: string): string[] {
  const normalized = skill.toLowerCase().replace(/[^a-z0-9+#.]+/g, " ").trim();
  const aliases: Record<string, string[]> = {
    "ruby on rails": ["ruby", "rails"],
    rails: ["ruby", "rails"],
    "vue 3": ["vue"],
    vue: ["vue"],
    "llm agents": ["llm", "agent", "agents", "ai", "claude", "cursor", "mcp"],
  };
  if (aliases[normalized]) return aliases[normalized];
  const stop = new Set(["and", "or", "with", "for", "the", "on", "of"]);
  return normalized.split(/\s+/).filter((token) => token.length >= 2 && !stop.has(token));
}

function queryHasToken(query: string, token: string): boolean {
  const low = query.toLowerCase();
  if (/^[a-z0-9]+$/.test(token)) {
    return new RegExp("(^|[^a-z0-9])" + escapeRegExp(token) + "([^a-z0-9]|$)").test(low);
  }
  return low.includes(token);
}

function missingQuerySkills(query: string, skills: string[]): string[] {
  return skills.filter((skill) => {
    const tokens = skillTokens(skill);
    return tokens.length > 0 && !tokens.some((token) => queryHasToken(query, token));
  });
}

function queryMatchesAnySkill(query: string, skills: string[]): boolean {
  return skills.some((skill) => {
    const tokens = skillTokens(skill);
    return tokens.length > 0 && tokens.some((token) => queryHasToken(query, token));
  });
}

function humanizeGap(value: string): string {
  return value
    .replace(/^Missing must-have evidence:\s*/i, "Missing must-have: ")
    .replace(/ was not visible in the copied profile text\.?/i, "")
    .replace(/^Seniority concern:\s*/i, "Seniority risk: ")
    .replace(/^Commercial concern:\s*/i, "Commercial risk: ")
    .replace(/^Extraction concern:\s*/i, "Extraction risk: ")
    .replace(/^Location mismatch:\s*/i, "Location mismatch: ");
}

type CleanupAction = "archive" | null;

function CleanupConfirm({
  noun,
  itemName,
  loading,
  onCancel,
  onConfirm,
}: {
  noun: string;
  itemName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={"Archive " + noun}
      className="rounded-xl border border-[#fecaca] bg-danger-soft p-4 text-sm"
    >
      <div className="font-semibold text-[#991b1b]">
        Archive this {noun}?
      </div>
      <p className="mt-1 text-[#7f1d1d]">
        This moves {itemName} out of active sourcing while keeping the record available for audit.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm} loading={loading}>
          Confirm archive {noun}
        </Button>
      </div>
    </div>
  );
}

function renderSearchFilters(run: SearchRun) {
  const filters = run.source_filters ?? {};
  const locations = readStringList(filters.locations ?? filters.location_names ?? filters.geo ?? filters.location);
  const geoUrns = readStringList(filters.geo_urns ?? filters.geoUrns ?? filters.geoUrn);
  const note = typeof filters.note === "string" ? filters.note : "";
  if (!locations.length && !geoUrns.length && !note) return null;
  const parts = [
    locations.length ? "Locations: " + locations.join(", ") : "",
    geoUrns.length ? "geoUrns: " + geoUrns.join(", ") : "",
    note ? "Notes: " + note : "",
  ].filter(Boolean);
  return <div className="mt-2 break-words text-sm text-text-muted">{parts.join(" · ")}</div>;
}

function readStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (typeof value === "string") return value.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean);
  return [];
}

function sourcingTaskNotice(task: SourcingTask): { title: string; message: string; className: string } {
  const error = task.error_message || "";
  const low = error.toLowerCase();
  const reason = task.status_reason || task.status;
  const label = task.status_label || humanize(reason);
  const danger = "border-[#fecaca] bg-danger-soft text-danger";
  const warning = "border-[#fde68a] bg-warning-soft text-[#854d0e]";
  const success = "border-[#bbf7d0] bg-success-soft text-success";
  const neutral = "border-border bg-surface-alt text-text-muted";

  if (reason === "queued") {
    return {
      title: label,
      message: "The LinkedIn sourcing evaluator is waiting to start. No outreach will be sent.",
      className: neutral,
    };
  }
  if (reason === "running") {
    return {
      title: label,
      message: "The evaluator is using the shared LinkedIn browser to inspect profiles and will stop after one candidate or a clear failure.",
      className: warning,
    };
  }
  if (reason === "completed") {
    return {
      title: label,
      message: task.result_summary || "One source-one attempt completed.",
      className: success,
    };
  }
  if (reason === "cancelled") {
    return {
      title: label,
      message: error || "The queued source-one attempt was cancelled before it started. Retry is available.",
      className: warning,
    };
  }
  if (reason === "blocked_login" || low.includes("authentication") || low.includes("sign-in") || low.includes("sign in") || low.includes("google")) {
    return {
      title: label,
      message: "Source-one failed: " + (error || "LinkedIn needs browser sign-in before sourcing can continue."),
      className: danger,
    };
  }
  if (reason === "checkpoint" || low.includes("checkpoint")) {
    return {
      title: label,
      message: "Source-one stopped for a LinkedIn checkpoint or verification wall: " + (error || "Resolve the checkpoint in the shared browser, then retry."),
      className: danger,
    };
  }
  if (reason === "no_results" || low.includes("no unsourced linkedin profile urls") || low.includes("no unsourced") || low.includes("search results")) {
    return {
      title: label,
      message: "Source-one failed: " + (error || "No unsourced LinkedIn profile URLs found in search results."),
      className: warning,
    };
  }
  if (reason === "all_attempts_exhausted" || low.includes("all inspected") || low.includes("skipped or unusable")) {
    return {
      title: label,
      message: "Source-one failed: " + (error || "All inspected LinkedIn profiles were skipped or unusable."),
      className: warning,
    };
  }
  return {
    title: label,
    message: "Source-one failed: " + (error || "The sourcing evaluator failed before producing a candidate."),
    className: danger,
  };
}

function safeTaskControlLabel(task: SourcingTask): string {
  const reason = task.status_reason || task.status;
  if (reason === "queued") return "Cancel available";
  if (reason === "running") return "Wait for finish";
  if (["failed", "blocked_login", "checkpoint", "no_results", "all_attempts_exhausted", "cancelled"].includes(reason)) {
    return "Retry available";
  }
  return "No action needed";
}

function hasTaskObservability(observability: TaskObservability): boolean {
  return Boolean(
    observability.attempts
      || observability.cooldown
      || observability.cooldownReason
      || observability.workerLogHref
      || observability.workerLogPath,
  );
}

function readTaskObservability(task: SourcingTask): TaskObservability {
  const raw = task as unknown as Record<string, unknown>;
  const attemptCount =
    readNumberField(raw, ["attempt_count", "attempt_number", "current_attempt"])
    ?? inferAttemptCount(task);
  const attemptLimit = readNumberField(raw, ["attempt_limit", "max_attempts"]);
  const attemptsRemaining = readNumberField(raw, ["attempts_remaining", "remaining_attempts"]);
  const cooldownSeconds = readNumberField(raw, ["cooldown_seconds_remaining", "retry_after_seconds"]);
  const cooldownUntil = readStringField(raw, ["cooldown_until", "retry_after_at", "retry_available_at", "next_retry_at"]);
  const cooldownReason = readStringField(raw, ["cooldown_reason", "retry_reason"]);
  const workerLogHref = normalizeTaskHref(readStringField(raw, [
    "latest_worker_log_url",
    "worker_log_url",
    "log_url",
    "latest_log_url",
  ]));
  const workerLogPath = readStringField(raw, [
    "latest_worker_log_path",
    "worker_log_path",
    "log_path",
    "latest_log_path",
  ]);
  const workerLogLabel = readStringField(raw, [
    "latest_worker_log_label",
    "worker_log_label",
    "log_label",
  ]);

  return {
    attempts: formatAttemptSummary(attemptCount, attemptLimit, attemptsRemaining),
    cooldown: formatCooldownSummary(cooldownSeconds, cooldownUntil),
    cooldownReason,
    workerLogHref,
    workerLogLabel,
    workerLogPath,
  };
}

function readNumberField(record: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function readStringField(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function inferAttemptCount(task: Pick<SourcingTask, "result_summary" | "error_message">): number | null {
  const combined = [task.result_summary, task.error_message].filter(Boolean).join(" ");
  if (!combined) return null;
  const acrossMatch = combined.match(/across\s+(\d+)\s+(?:x-ray\s+)?search attempt\(s\)/i);
  if (acrossMatch) return Number(acrossMatch[1]);
  const noteMatches = [...combined.matchAll(/(^|\|\s*)(\d+)\.\s/gm)];
  if (!noteMatches.length) return null;
  return noteMatches.reduce((max, match) => Math.max(max, Number(match[2] || 0)), 0) || null;
}

function formatAttemptSummary(
  attemptCount: number | null,
  attemptLimit: number | null,
  attemptsRemaining: number | null,
): string | null {
  if (attemptCount == null && attemptLimit == null && attemptsRemaining == null) return null;
  if (attemptCount != null && attemptLimit != null) {
    let summary = `${attemptCount} of ${attemptLimit} search attempt${attemptLimit === 1 ? "" : "s"}`;
    if (attemptsRemaining != null) summary += ` · ${attemptsRemaining} left`;
    return summary;
  }
  if (attemptCount != null) {
    let summary = `${attemptCount} search attempt${attemptCount === 1 ? "" : "s"}`;
    if (attemptsRemaining != null) summary += ` · ${attemptsRemaining} left`;
    return summary;
  }
  return attemptsRemaining != null ? `${attemptsRemaining} attempt${attemptsRemaining === 1 ? "" : "s"} left` : null;
}

function formatCooldownSummary(cooldownSeconds: number | null, cooldownUntil: string | null): string | null {
  const parts: string[] = [];
  if (cooldownSeconds != null && cooldownSeconds > 0) {
    parts.push("Retry in " + humanizeDuration(cooldownSeconds));
  }
  if (cooldownUntil) {
    parts.push("Until " + fmtDate(cooldownUntil));
  }
  return parts.length ? parts.join(" · ") : null;
}

function humanizeDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function normalizeTaskHref(value: string | null): string | null {
  if (!value) return null;
  if (/^[a-z]+:\/\//i.test(value)) return value;
  if (value.startsWith("/api/")) return apiPath(value);
  return value;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^\${}()|[\]\\]/g, "\\$&");
}
